import React, { forwardRef, useRef } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import * as CommonIcons from 'assets/common';
import { classNames, getDataPropsFromRest } from 'utilities/Utils';
import { StyledEngineProvider } from '@mui/material';

type IndicatorPosition = 'start' | 'center' | 'end';

type CarouselProps = {
  children: React.ReactNode | React.ReactNode[];
  showScrollIcon?: boolean;
  indicatorPosition?: IndicatorPosition;
  onLeftClick?: Function;
  onRightClick?: Function;
  showIndicator?: boolean;
  itemClass?: string;
  customButtons?: {
    left: React.ReactNode | React.ReactNode[];
    right: React.ReactNode | React.ReactNode[];
    leftClass?: string;
    rightClass?: string;
  };
  alwaysShowArrows?: boolean;
  className?: string;
  showBothButtons?: boolean;
  showDots?: boolean;
};

type StyleProps = {
  indicatorPosition: IndicatorPosition;
};
const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    position: 'relative',
    width: '100%',
  },
  slider: {
    width: 'inherit',
  },
  slides: {
    width: 'inherit',
    display: 'flex',
    overflow: 'hidden',
  },
  slide: {
    flexShrink: 0,
    width: 'inherit',
    borderRadius: '4px',
  },
  lines: ({ indicatorPosition }: StyleProps) => {
    let justifyContent = 'center';
    if (indicatorPosition === 'start') {
      justifyContent = 'flex-start';
    } else if (indicatorPosition === 'end') {
      justifyContent = 'flex-end';
    }
    return {
      display: 'flex',
      justifyContent,
      width: '100%',
      marginTop: '14px',
      '& > div': {
        width: '40px',
        height: '4px',
        marginRight: '8px',
        borderRadius: '4px',
        backgroundColor: theme.palette.primary.light,
        cursor: 'pointer',
      },
    };
  },
  primary: {
    backgroundColor: `${theme.palette.primary.main}!important`,
  },
  nextPrev: {
    cursor: 'pointer',
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
  },
  left: {
    left: '-32px',
    zIndex: 1, //not affecting current component
  },
  right: {
    right: '-32px',
  },
}));

const CarouselComp = (
  props: CarouselProps,
  ref?: React.Ref<HTMLDivElement>,
) => {
  const {
    children,
    showScrollIcon = true,
    indicatorPosition = 'center',
    onLeftClick = () => {},
    onRightClick = () => {},
    showIndicator = true,
    itemClass = '', //className for items
    customButtons = null, //adding custom btns, existing are diff from design,
    className = '',
    showBothButtons = false,
    showDots = false,
    ...rest
  } = props;

  const dataProps = getDataPropsFromRest(rest);

  const [activeItem, setActiveItem] = React.useState(0);

  const sliderRef = useRef<HTMLDivElement>();

  const goto = (id, slide) => {
    const element: HTMLDivElement = sliderRef.current?.querySelector(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
      setActiveItem(slide);
    }
  };
  const handleNavigation = id => goto(`#slide_${id}`, id);

  const classes = useStyles({ indicatorPosition });
  return (
    <StyledEngineProvider injectFirst>
      <div ref={ref} className={classNames(classes.root)} {...dataProps}>
        {showScrollIcon && (showBothButtons || activeItem > 0) && (
          <div
            className={classNames(
              classes.nextPrev,
              classes.left,
              customButtons?.leftClass,
              activeItem === 0 ? 'cursor-not-allowed' : '',
            )}
            onClick={() => {
              if (activeItem === 0) return;
              handleNavigation(activeItem - 1);
              onLeftClick(activeItem - 1);
            }}
          >
            {customButtons ? customButtons.left : <CommonIcons.ChevronLeft />}
          </div>
        )}
        <div className={classes.slider}>
          <div
            className={classNames(classes.slides, className)}
            ref={sliderRef}
          >
            {Array.isArray(children) ? (
              children.map((child, index) => (
                <div
                  className={classNames(classes.slide, itemClass)}
                  key={`slide_${index}`}
                  id={`slide_${index}`}
                >
                  {child}
                </div>
              ))
            ) : (
              <div className={classes.slide}>{children}</div>
            )}
          </div>
          {showIndicator ? (
            Array.isArray(children) &&
            children.length > 1 && (
              <div className={classes.lines}>
                {children.map((_, index) => (
                  <div
                    className={classNames(
                      activeItem === index && classes.primary,
                      showDots && '!w-2 !h-2',
                    )}
                    onClick={() => goto(`#slide_${index}`, index)}
                  />
                ))}
              </div>
            )
          ) : (
            <></>
          )}
        </div>
        {showScrollIcon &&
          Array.isArray(children) &&
          (showBothButtons || activeItem !== children.length - 1) && (
            <div
              className={classNames(
                classes.nextPrev,
                classes.right,
                customButtons?.rightClass,
                activeItem + 1 === children.length ? 'cursor-not-allowed' : '',
              )}
              onClick={() => {
                if (activeItem + 1 === children.length) return;
                handleNavigation(activeItem + 1);
                onRightClick(activeItem + 1);
              }}
            >
              {customButtons ? (
                customButtons.right
              ) : (
                <CommonIcons.ChevronRight />
              )}
            </div>
        )}
      </div>
    </StyledEngineProvider>
  );
};

const Carousel = forwardRef(CarouselComp);
export { Carousel };
